// components:RangeTemplateSelector:index.tsx

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, Text, Select, Group, Button, Modal, Table, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconTemplate } from '@tabler/icons-react';

import { rangeTemplateService, RangeTemplateItem } from 'services/lia/rangeTemplateService';

/* eslint-disable */
interface RangeTemplateSelectorProps {
  onSelect: (items: RangeTemplateItem[]) => void;
}
/* eslint-enable */

const RangeTemplateSelector: React.FC<RangeTemplateSelectorProps> = ({ onSelect }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  // Fetch templates for dropdown
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['rangeTemplates'],
    queryFn: () => rangeTemplateService.getList({ pageSize: 100 }),
    staleTime: 60000
  });

  // Update selected template when selection changes
  useEffect(() => {
    if (selectedTemplateId && templatesData?.data) {
      const template = templatesData.data.find(
        (t: any) => t.msRangeTemplateID === selectedTemplateId
      );

      if (template) {
        setSelectedTemplate(template);
      }
    } else {
      setSelectedTemplate(null);
    }
  }, [selectedTemplateId, templatesData]);

  const handleApplyTemplate = () => {
    if (selectedTemplate?.items && selectedTemplate.items.length > 0) {
      onSelect(selectedTemplate.items);
      close();
    }
  };

  return (
    <>
      <Button
        variant='light'
        color='#7B2183'
        w={'full'}
        leftSection={<IconTemplate size={16} />}
        onClick={open}
        loading={isLoading}
      >
        Use Range Template
      </Button>

      <Modal opened={opened} onClose={close} title='Select Range Template' size='lg'>
        <Stack gap='md'>
          <Text size='sm'>Select a template to apply its range options to your question.</Text>

          <Select
            label='Template'
            placeholder='Select a template'
            data={(templatesData?.data || []).map((t: any) => ({
              value: t.msRangeTemplateID || '',
              label: t.templateName + (t.isDefault ? ' (Default)' : '')
            }))}
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            searchable
            clearable
          />

          {selectedTemplate && selectedTemplate.items && selectedTemplate.items.length > 0 && (
            <>
              <Text size='sm' fw={500}>
                Preview:
              </Text>

              <ScrollArea h={200}>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th>Sequence</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {selectedTemplate.items.map((item: any, index: number) => (
                      <Table.Tr key={index}>
                        <Table.Td>{item.label}</Table.Td>
                        <Table.Td>{item.value}</Table.Td>
                        <Table.Td>{item.sequence}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>

              {selectedTemplate.description && (
                <Text size='sm' color='dimmed'>
                  {selectedTemplate.description}
                </Text>
              )}
            </>
          )}

          <Group justify='flex-end' mt='lg'>
            <Button variant='outline' onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyTemplate}
              disabled={
                !selectedTemplate || !selectedTemplate.items || selectedTemplate.items.length === 0
              }
            >
              Apply Template
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default RangeTemplateSelector;
